import { useCallback } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { Paper } from "@mui/material";
import cx from "classnames";
import ArrowIcon from "src/components/general/ArrowIcon/ArrowIcon";
import { useClickOutside } from "src/hooks/useClickOutside";
import ComparativeAnalysisForm from "../../ComparativeAnalysisForm";
import { useSearchParamsGroups } from "../../hooks/useComparativeAnalysisSearchParams";
import ComparativeAnalysisGroupsSwapBtn from "../ComparativeAnalysisGroupsSwapBtn/ComparativeAnalysisGroupsSwapBtn";
import { useComparativeAnalysisFormDropdownOpen } from "./ComparativeAnalysisFormDropdown.atoms";
import ComparativeAnalysisFormSelectPopover from "./components/ComparativeAnalysisFormSelectPopover/ComparativeAnalysisFormSelectPopover";
import styles from "./ComparativeAnalysisFormDropdown.module.css";

const POPOVER_WRAPPER_CLASSNAME = "flex-1 max-w-[48%]";

const ComparativeAnalysisFormDropdown = () => {
  const { group1, group2 } = useSearchParamsGroups();
  const [open, setOpen] = useComparativeAnalysisFormDropdownOpen();

  const toggleOpen = useCallback(() => {
    setOpen((s) => !s);
  }, [setOpen]);

  const close = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const onSubmit = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  useHotkeys("esc", close, {
    enabled: open,
  });
  const handleOnMouseDownCapture = useClickOutside(close);

  return (
    <div
      onMouseDownCapture={handleOnMouseDownCapture}
      className={cx(
        "relative z-20 flex-1 flex gap-2 items-center",
        styles.ComparativeAnalysisFormDropdown
      )}
    >
      {/* Dropdown's content: */}
      <div className={POPOVER_WRAPPER_CLASSNAME} onClick={toggleOpen}>
        <ComparativeAnalysisFormSelectPopover
          className="border-base-color! w-full!"
          type="base"
          group={group1}
        />
      </div>

      <ComparativeAnalysisGroupsSwapBtn />

      <div className={POPOVER_WRAPPER_CLASSNAME} onClick={toggleOpen}>
        <ComparativeAnalysisFormSelectPopover
          className="border-compare-color! w-full!"
          type="compare"
          group={group2}
        />
      </div>

      {/* Dropdowns' popover: */}
      <Paper
        elevation={6}
        className={cx(
          "comparative-analysis-form-dropdown-popover absolute z-50 left-0 right-0 -top-4 p-4 transition-all! duration-300! ease-out! -translate-y-full",
          { "top-0 translate-none open": open }
        )}
      >
        <div
          className="flex justify-center items-center -m-4 -mt-6 p-2 mb-3 cursor-pointer hover:bg-background-default h-(--top-bar-height)"
          onClick={toggleOpen}
        >
          <ArrowIcon up size={16} />
        </div>

        <ComparativeAnalysisForm
          // force re-render when the dropdown is opened/closed to re-mount in order to reset its internal state
          // (in case fields' values were changed but the form was not "submitted")
          key={open ? "open" : "closed"}
          formOnly
          onSubmit={onSubmit}
        />
      </Paper>
    </div>
  );
};

export default ComparativeAnalysisFormDropdown;
